import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LeadAgentChatBannerService } from './leadAgentChatBanner.service';

// Whether the Owner closed the Lead Agent pause notice of the chat for the whole
// workspace (AGI-766, MLA-100). Read only while the pause is commercial, so a
// workspace whose Lead Agent is not paused makes no request. Fail-open: loading,
// an error or any other answer reads as not closed, which shows the notice as
// before. Read again on window focus, so a close the Owner made on another
// device reaches this tab. A close lasts as long as the commercial pause it
// closed: when that pause ends, this tab forgets it.
//
// It makes an HTTP request: call it once (`useChat` does) and read the chat context.

export type tUseLeadAgentChatBanner = {
  dismissed: boolean;
  /** The Owner's close just succeeded here: hide the notice without waiting for a read. */
  markDismissed: () => void;
};

export type tUseLeadAgentChatBannerOptions = {
  workspaceId: string | undefined;
  enabled: boolean;
};

type tDismissedState = { workspaceId: string | undefined; dismissed: boolean };

/**
 * The workspace's close of the Lead Agent pause notice of the chat.
 *
 * @param options - The current workspace and whether the pause is commercial.
 * @returns Whether the notice is closed, and `markDismissed`.
 */
export function useLeadAgentChatBanner(options: tUseLeadAgentChatBannerOptions): tUseLeadAgentChatBanner {
  const { workspaceId, enabled } = options;
  // Keyed by workspace: another workspace's close never carries over, not even
  // until its own read answers.
  const [state, setState] = useState<tDismissedState>({ workspaceId, dismissed: false });
  const dismissed = state.workspaceId === workspaceId && state.dismissed;
  // A read that started before a close confirmed here answers with an older
  // state, so it must not show the notice again: the close cancels the read in
  // flight and counts itself, and a read applies only if no close came after it.
  const pendingReadRef = useRef<AbortController | null>(null);
  const closesRef = useRef(0);

  const markDismissed = useCallback(() => {
    closesRef.current += 1;
    pendingReadRef.current?.abort();
    setState({ workspaceId, dismissed: true });
  }, [workspaceId]);

  useEffect(() => {
    if (!enabled || !workspaceId) {
      // The commercial pause ended, and its close with it: the next one starts as
      // not closed until its own read answers, so a failed read shows the notice.
      setState((previous) => (previous.dismissed ? { workspaceId, dismissed: false } : previous));
      return;
    }
    let active = true;

    const read = () => {
      pendingReadRef.current?.abort();
      const controller = new AbortController();
      pendingReadRef.current = controller;
      const closesAtStart = closesRef.current;
      LeadAgentChatBannerService.getDismissed(workspaceId, controller.signal)
        .then((value) => {
          if (active && closesRef.current === closesAtStart) setState({ workspaceId, dismissed: value });
        })
        .catch((e: unknown) => {
          const name = (e as { name?: string })?.name;
          // A superseded read or the cleanup aborts; a real error keeps what this
          // tab already knows, which starts as not closed (fail-open).
          if (name === 'CanceledError' || name === 'AbortError') return;
        });
    };

    read();
    window.addEventListener('focus', read);

    return () => {
      active = false;
      pendingReadRef.current?.abort();
      window.removeEventListener('focus', read);
    };
  }, [enabled, workspaceId]);

  // Stable while nothing changed: the chat context value lists it as a dependency.
  return useMemo(() => ({ dismissed, markDismissed }), [dismissed, markDismissed]);
}
